import { MultiLangString } from '../MultiLangString';

/**
 * Price entry for a subscription - stores price at time of purchase
 */
export interface HcSubscriptionPrice {
  currency: string; // 'USD', 'CAD', 'EUR', 'GBP'
  price: number;
  emergencyCallCost?: number; // Optional per-currency emergency call cost
}

/**
 * Price entry for a setupfee or other one-time fee
 */
export interface HcPrice {
  currency: string; // 'USD', 'CAD', 'EUR', 'GBP'
  price: number;
}

/**
 * Price entry for a subscription plan - stores current pricing
 */
export interface HcSubscriptionPlanPrice {
  currency: string;
  price: number;
  stripePriceId?: string; // Stripe price ID for this currency
  emergencyCallCost?: number;
}

/**
 * Subscription document - stores subscription-specific data
 * References plan configuration via planType
 */
export interface HcSubscription {
  _id: string;
  csId: string;
  planType: HcSubscriptionPlanType; // Reference to plan configuration
  prices: HcSubscriptionPrice[]; // Prices at time of purchase (historical record) - one per currency
  currency: string; // Currency used at purchase time ('USD', 'CAD', 'EUR', 'GBP')
  stripeSubscriptionId?: string; // Stripe subscription ID for recurring billing
  stripeCustomerId?: string; // Stripe customer ID
  stripePriceId?: string; // Stripe price ID for the subscription (backward compatibility - references price ID for selected currency)
  stripePriceIds?: Record<string, string>; // Stripe price IDs for all currencies
  cardLast4?: string; // Last 4 digits of the credit card on file
  cardExpMonth?: number; // Expiry month of the credit card (1-12)
  cardExpYear?: number; // Expiry year of the credit card (e.g., 2025)
  startDate: Date;
  endDate?: Date; // Optional end date for time-limited subscriptions
  status: HcSubscriptionStatus;
  cancelAtPeriodEnd?: boolean; // True if subscription is scheduled to cancel at period end
  createdAt: Date;
  updatedAt: Date;
  // Legacy fields for backward compatibility (deprecated)
  /** @deprecated Use prices array instead */
  price?: number;
  /** @deprecated Use prices array instead */
  emergencyCallCost?: number;
}

/**
 * Plan configuration document - stored in separate collection
 * Can be updated independently without affecting existing subscriptions
 */
export interface HcSubscriptionPlan {
  _id: string;
  planType: HcSubscriptionPlanType;
  prices: HcSubscriptionPlanPrice[]; // Current prices in all supported currencies
  setupFee?: HcPrice[]; // One-time setup fee per currency (optional)
  billingPeriod: HcSubscriptionBillingPeriod;
  callsPerDay: number; // Number of calls allowed per day (0 = per-call pricing)
  freeTestCallsPerMonth: number; // Number of free test calls allowed per month (0 = no free test calls)
  maxNotificationRecipients: number; // Maximum number of people who can receive notifications
  maxCallbacks: number; // Maximum number of callbacks if no answer (0 = unlimited or not applicable)
  description: MultiLangString;
  isActive: boolean; // Whether this plan is currently available for purchase
  /** When false, plan is omitted from GET subscription/plans and cannot be purchased via API (DB allocation only). Missing = visible. */
  visibleInPublicCatalog?: boolean;
  createdAt: Date;
  updatedAt: Date;
  // Legacy fields for backward compatibility (deprecated)
  /** @deprecated Use prices array instead */
  price?: number;
  /** @deprecated Use prices array instead */
  emergencyCallCost?: number;
}

export enum HcSubscriptionPlanType {
  PAY_AS_YOU_CARE = 'HcSubscriptionPlanType.payAsYouCare',
  /** Same limits as PAY_AS_YOU_CARE; hidden from catalog; assign via Mongo only. */
  COMPLIMENTARY_PAY_AS_YOU_CARE = 'HcSubscriptionPlanType.complimentaryPayAsYouCare',
  DAILY_REASSURANCE = 'HcSubscriptionPlanType.dailyReassurance',
  PREMIUM_PEACE_OF_MIND = 'HcSubscriptionPlanType.premiumPeaceOfMind',
  STEADY_CARE_PLAN = 'HcSubscriptionPlanType.steadyCarePlan',
  GUARDIAN_PLUS = 'HcSubscriptionPlanType.guardianPlus',
}

export enum HcSubscriptionBillingPeriod {
  PER_CALL = 'HcSubscriptionBillingPeriod.perCall',
  MONTHLY = 'HcSubscriptionBillingPeriod.monthly',
  THREE_MONTHS = 'HcSubscriptionBillingPeriod.threeMonths',
}

export enum HcSubscriptionStatus {
  ACTIVE = 'HcSubscriptionStatus.active',
  INACTIVE = 'HcSubscriptionStatus.inactive',
  EXPIRED = 'HcSubscriptionStatus.expired',
  CANCELLED = 'HcSubscriptionStatus.cancelled',
  PASTDUE = 'HcSubscriptionStatus.pastDue',
}

/** Limits shared by Pay-as-you-care and complimentary Pay-as-you-care (must never drift). */
export const hcPayAsYouCareSharedLimits = {
  billingPeriod: HcSubscriptionBillingPeriod.PER_CALL,
  callsPerDay: 0, // 0 means per-call pricing
  maxNotificationRecipients: 2,
  maxCallbacks: 3,
  description: {
    en: 'Per call pricing - schedule calls as often as needed, anytime',
    fr: 'Tarification par appel - planifiez des appels aussi souvent que nécessaire, à tout moment',
  },
} as const;

/**
 * Plan types returned by the public subscription catalog API and config-only fallback
 * (excludes DB-only tiers such as complimentary Pay-as-you-care).
 */
export const HC_PUBLIC_SUBSCRIPTION_PLAN_TYPES: readonly HcSubscriptionPlanType[] = [
  HcSubscriptionPlanType.PAY_AS_YOU_CARE,
  HcSubscriptionPlanType.DAILY_REASSURANCE,
  HcSubscriptionPlanType.PREMIUM_PEACE_OF_MIND,
  HcSubscriptionPlanType.STEADY_CARE_PLAN,
  HcSubscriptionPlanType.GUARDIAN_PLUS,
];

/**
 * Default subscription plan configuration constants
 * These represent the initial/default plan configurations
 * In production, these should be stored in the HcSubscriptionPlan collection
 * and can be updated independently of subscriptions
 */
export const HcSubscriptionPlanConfig: Record<HcSubscriptionPlanType, {
  billingPeriod: HcSubscriptionBillingPeriod;
  callsPerDay: number;
  maxNotificationRecipients: number;
  maxCallbacks: number;
  description: MultiLangString;
}> = {
  [HcSubscriptionPlanType.PAY_AS_YOU_CARE]: {
    billingPeriod: hcPayAsYouCareSharedLimits.billingPeriod,
    callsPerDay: hcPayAsYouCareSharedLimits.callsPerDay,
    maxNotificationRecipients: hcPayAsYouCareSharedLimits.maxNotificationRecipients,
    maxCallbacks: hcPayAsYouCareSharedLimits.maxCallbacks,
    description: { ...hcPayAsYouCareSharedLimits.description },
  },
  [HcSubscriptionPlanType.COMPLIMENTARY_PAY_AS_YOU_CARE]: {
    billingPeriod: hcPayAsYouCareSharedLimits.billingPeriod,
    callsPerDay: hcPayAsYouCareSharedLimits.callsPerDay,
    maxNotificationRecipients: hcPayAsYouCareSharedLimits.maxNotificationRecipients,
    maxCallbacks: hcPayAsYouCareSharedLimits.maxCallbacks,
    description: { ...hcPayAsYouCareSharedLimits.description },
  },
  [HcSubscriptionPlanType.DAILY_REASSURANCE]: {
    billingPeriod: HcSubscriptionBillingPeriod.MONTHLY,
    callsPerDay: 1,
    maxNotificationRecipients: 3,
    maxCallbacks: 0, // Not specified in requirements
    description: {
      en: 'Monthly plan with 1 call per day',
      fr: 'Forfait mensuel avec 1 appel par jour',
    },
  },
  [HcSubscriptionPlanType.PREMIUM_PEACE_OF_MIND]: {
    billingPeriod: HcSubscriptionBillingPeriod.MONTHLY,
    callsPerDay: 2,
    maxNotificationRecipients: 5,
    maxCallbacks: 0, // Not specified in requirements
    description: {
      en: 'Monthly plan with 2 calls per day',
      fr: 'Forfait mensuel avec 2 appels par jour',
    },
  },
  [HcSubscriptionPlanType.STEADY_CARE_PLAN]: {
    billingPeriod: HcSubscriptionBillingPeriod.THREE_MONTHS,
    callsPerDay: 1,
    maxNotificationRecipients: 3,
    maxCallbacks: 0, // Not specified in requirements
    description: {
      en: '3-month bundle of Daily Reassurance plan',
      fr: 'Forfait de 3 mois du plan Réconfort quotidien',
    },
  },
  [HcSubscriptionPlanType.GUARDIAN_PLUS]: {
    billingPeriod: HcSubscriptionBillingPeriod.THREE_MONTHS,
    callsPerDay: 2,
    maxNotificationRecipients: 6,
    maxCallbacks: 0, // Not specified in requirements
    description: {
      en: '3-month bundle of Premium Peace of Mind plan',
      fr: 'Forfait de 3 mois du plan Tranquillité d\'esprit premium',
    },
  },
};

